import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Collapse, Grid, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import Typography from "@mui/material/Typography";
import { DataGrid, GridRow } from "@mui/x-data-grid";
import React, { useEffect, useMemo, useState, useRef } from "react";
import { randomNumber, stringToLong } from "../util/constant";

// Sorts values ignoring -ve sign (absolute values)
const absoluteSortComparator = (v1, v2) => {
  if (!v1) v1 = 0
  if (!v2) v2 = 0
  if (!v1 && !v2) {
    v1 = 1
    v2 = 0
  }
  return Math.abs(v1) - Math.abs(v2)
}

const CustomRow = (params) => {
  const shortListed = params?.row?.shortListedTrades
  const functions = params?.row?.functions

  if (!shortListed || shortListed?.length === 0)
    return <GridRow {...params} />

  return <>
    <Box component='div' display='flex' flexDirection='row'>
      <GridRow {...params} />
    </Box>
    <Collapse in={params?.row?.open || false}>
      <DhShortlisted shortListedTrades={shortListed} deleteShortListedTrade={functions?.deleteShortListedTrade} />
    </Collapse>
  </>
}

export function isNumeric(n) {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

const getClassName = (params, field) => {
  return params.field === field && params.value > 0 ? 'profit' : 'loss';
}

const toFixed2 = value => {
  if (isNaN(value) || !value)
    return ''
  return value?.toFixed(2)
}

//        <a href={`https://kite.zerodha.com/chart/web/ciq/BSE/${params?.value}/408065"`} target="_blank">

const WatchListData = ({ customToolBar, watchList, setWatchList, watchListLiveMap, setUpdateAllGrids, setWarningMessage,
  shortListedTrades, openTrades, setShortListedTrades, setOpen, user, isLoading, setIsLoadingWatchList, deleteWatchListIemFromUI, setTotalPnl }) => {
  const formRef = useRef(null);

  const columns = [
    {
      field: 'action', headerName: '', minWidth: 25, width: 25, align: 'left',
      renderCell: params => {
        // Manipulate the row object inside datagrid to set toggle the 'open' property boolean
        // This open property will expand and close the sub-row.
        const shortListedTradesForStock = params?.row?.shortListedTrades?.length
        if (!shortListedTradesForStock || shortListedTradesForStock === 0)
          return <></>
        return <Box component='div' backgroundColor='#80b918' borderRadius={5} paddingX={0.3} margin={0} sx={{ cursor: 'pointer' }}
          onClick={() => {
            params.row.open = !params.row.open
            params.api.updateRows([params?.row])
          }
          }>{params?.row?.shortListedTrades?.length}</Box>
      }
    },
    {
      field: 'symbol', headerName: 'Symbol', width: 120, type: 'string', align: 'left',
      renderCell: (params) => <a href={`https://kite.zerodha.com/chart/web/ciq/NSE/${params?.value?.replaceAll("-EQ", "")}/${params?.row?.zerodhaTokenId}`} target="_blank" rel="noreferrer">
        {params.value}
      </a>
    },
    {
      field: 'ltp', headerName: 'LTP', type: 'number', align: 'right', headerAlign: 'left', width: 75,
      valueGetter: (params) => {
        const price = watchListLiveMap?.[params?.row?.tokenId]?.last_traded_price
        return toFixed2(price / 100)
      }
    },
    {
      field: 'percentFromOpen',
      headerName: '% Ch O',
      type: 'number',
      align: 'right',
      headerAlign: 'left',
      width: 75,
      valueGetter: (params) => {
        const open = watchListLiveMap?.[params?.row?.tokenId]?.open_price_day / 100;
        const ltp = watchListLiveMap?.[params?.row?.tokenId]?.last_traded_price / 100
        if (!open || !ltp || open === 0) {
          return
        }
        return toFixed2((ltp - open) / open * 100)
      },
      sortComparator: absoluteSortComparator
    },
    {
      field: 'totalBuyQty', headerName: 'Tot B V', type: 'number', align: 'right', headerAlign: 'left', width: 70,
      valueGetter: (params) => {
        const price = watchListLiveMap?.[params?.row?.tokenId]?.total_buy_quantity
        return parseInt(price / 100)
      }
    },
    {
      field: 'totalSellQty', headerName: 'Tot S V', type: 'number', align: 'right', headerAlign: 'left', width: 70,
      valueGetter: (params) => {
        const price = watchListLiveMap?.[params?.row?.tokenId]?.total_sell_quantity
        return parseInt(price / 100)
      }
    },
    {
      field: 'volTraded', headerName: 'Trd V (Cr)', type: 'number', align: 'right', headerAlign: 'left', width: 70,
      valueGetter: (params) => {
        const ltp = watchListLiveMap?.[params?.row?.tokenId]?.last_traded_price / 100
        if (!!!ltp) {
          return
        }
        const totalTradedValue = stringToLong(watchListLiveMap?.[params?.row?.tokenId]?.vol_traded) * ltp
        const volumeInCr = totalTradedValue / 10000000;
        return (volumeInCr < 1)
          ? volumeInCr.toFixed(2)
          : volumeInCr.toFixed(0)
      }
    },
    {
      field: 'buyZerodhaAction', headerName: 'BuyZ', width: 15,
      valueGetter: (params) => {
        return `${params.row?.tokenId}`;
      },
      renderCell: (params) => (
        <>
          <Button style={{ maxWidth: '30px', maxHeight: '20px', minWidth: '15px', minHeight: '10px' }}
            color={"primary"} variant={"contained"} size={"small"} onClick={event => handleZerodhaAction(event, params.row, "BUY")}>
            <Typography fontSize={"xx-small"}>BZ</Typography>
          </Button>
        </>
      ),
    },
    {
      field: 'sellZerodhaAction', headerName: 'SellZ', width: 15,
      valueGetter: (params) => {
        return `${params.row?.tokenId}`;
      },
      renderCell: (params) => (
        <>
          <Button style={{ maxWidth: '30px', maxHeight: '20px', minWidth: '15px', minHeight: '10px' }}
            color={"secondary"} variant={"contained"} size={"small"} onClick={event => handleZerodhaAction(event, params.row, "SELL")}>
            <Typography fontSize={"xx-small"}>SZ</Typography>
          </Button>
        </>
      ),
    },
    {
      field: 'open', headerName: 'Open', type: 'number', align: 'right', headerAlign: 'left', width: 75,
      valueGetter: (params) => {
        const value = watchListLiveMap?.[params?.row?.tokenId]?.open_price_day / 100
        return toFixed2(value)
      }
    },
    {
      field: 'percentFromClose',
      headerName: '% Ch C',
      ptype: 'number',
      align: 'right',
      headerAlign: 'left',
      width: 75,
      valueGetter: (params) => {
        const close = watchListLiveMap?.[params?.row?.tokenId]?.close_price / 100;
        const ltp = watchListLiveMap?.[params?.row?.tokenId]?.last_traded_price / 100
        return toFixed2((ltp - close) / close * 100)
      },
      sortComparator: absoluteSortComparator
    },
    {
      field: 'quantity', headerName: 'Qty', width: "80", type: 'number',
      editable: true, align: 'right', headerAlign: 'left',
      valueSetter: (params) => {
        let data = {
          id: params.row?.id,
          userId: params.row?.userId,
          symbol: params.row?.symbol,
          tokenId: params.row?.tokenId,
          userTradeStrategy: params.row?.userTradeStrategy,
          createdOn: params.row?.createdOn,
          triggerPrice: params.row?.triggerPrice,
          fixedStoplossAbsolute: params.row?.fixedStoplossAbsolute,
          fixedStoplossPercent: params.row?.fixedStoplossPercent,
          trailingStoplossPercent: params.row?.trailingStoplossPercent,
          trailingStoplossAbsolute: params.row?.trailingStoplossAbsolute,
          quantity: params.value
        }
        return data;
      }
    },
    {
      field: 'buyAction', headerName: 'Buy', width: 15,
      valueGetter: (params) => {
        return `${params.row?.tokenId}`;
      },
      renderCell: (params) => (
        <>
          <Button style={{ maxWidth: '30px', maxHeight: '20px', minWidth: '15px', minHeight: '10px' }}
            color={"primary"} variant={"contained"} size={"small"} onClick={event => shortListTrade(event, params.row, "BUY")}>
            <Typography fontSize={"xx-small"}>B</Typography>
          </Button>
        </>
      ),
    },
    {
      field: 'sellAction', headerName: 'Sell', width: '15',
      valueGetter: (params) => {
        return `${params.row?.tokenId}`;
      },
      renderCell: (params) => (
        <Button style={{ maxWidth: '30px', maxHeight: '20px', minWidth: '15px', minHeight: '10px' }}
          color={"secondary"} variant={"contained"} size={"small"} onClick={event => shortListTrade(event, params.row, "SELL")}>
          <Typography fontSize={"xx-small"}>S</Typography>
        </Button>
      ),
    },
    {
      field: 'triggerPrice', headerName: 'Trig %', width: 100, type: 'number',
      editable: true, align: 'right', headerAlign: 'left',
      valueSetter: (params) => {
        let data = {
          id: params.row?.id,
          userId: params.row?.userId,
          symbol: params.row?.symbol,
          tokenId: params.row?.tokenId,
          userTradeStrategy: params.row?.userTradeStrategy,
          createdOn: params.row?.createdOn,
          quantity: params.row?.quantity,
          fixedStoplossAbsolute: params.row?.fixedStoplossAbsolute,
          fixedStoplossPercent: params.row?.fixedStoplossPercent,
          trailingStoplossPercent: params.row?.trailingStoplossPercent,
          trailingStoplossAbsolute: params.row?.trailingStoplossAbsolute,
          triggerPrice: params.value
        }
        return data;
      }
    },
    {
      field: 'trailingStoplossAbsolute', headerName: 'Tr SL', width: "80", type: 'number',
      editable: true, align: 'center', headerAlign: 'left',
      valueSetter: (params) => {
        let data = {
          id: params.row?.id,
          userId: params.row?.userId,
          symbol: params.row?.symbol,
          tokenId: params.row?.tokenId,
          userTradeStrategy: params.row?.userTradeStrategy,
          createdOn: params.row?.createdOn,
          triggerPrice: params.row?.triggerPrice,
          quantity: params.row?.quantity,
          fixedStoplossAbsolute: params.row?.fixedStoplossAbsolute,
          fixedStoplossPercent: params.row?.fixedStoplossPercent,
          trailingStoplossPercent: null,
          trailingStoplossAbsolute: params.value,
        }
        return data;
      }
    },
    {
      field: 'trailingStoplossPercent', headerName: '% Tr SL', width: "80", type: 'number',
      editable: true, align: 'center', headerAlign: 'left',
      valueSetter: (params) => {
        let data = {
          id: params.row?.id,
          userId: params.row?.userId,
          symbol: params.row?.symbol,
          tokenId: params.row?.tokenId,
          userTradeStrategy: params.row?.userTradeStrategy,
          createdOn: params.row?.createdOn,
          triggerPrice: params.row?.triggerPrice,
          quantity: params.row?.quantity,
          fixedStoplossAbsolute: params.row?.fixedStoplossAbsolute,
          fixedStoplossPercent: params.row?.fixedStoplossPercent,
          trailingStoplossAbsolute: null,
          trailingStoplossPercent: params.value
        }
        return data;
      }
    },
    {
      field: 'fixedStoplossAbsolute', headerName: 'FixSL', width: "80", type: 'number',
      editable: true, align: 'center', headerAlign: 'left',
      valueSetter: (params) => {
        let data = {
          id: params.row?.id,
          userId: params.row?.userId,
          symbol: params.row?.symbol,
          tokenId: params.row?.tokenId,
          userTradeStrategy: params.row?.userTradeStrategy,
          createdOn: params.row?.createdOn,
          triggerPrice: params.row?.triggerPrice,
          quantity: params.row?.quantity,
          trailingStoplossAbsolute: params?.row?.trailingStoplossAbsolute,
          trailingStoplossPercent: params?.row?.trailingStoplossPercent,
          fixedStoplossPercent: null,
          fixedStoplossAbsolute: params.value
        }
        return data;
      }
    },
    {
      field: 'fixedStoplossPercent', headerName: '% FixSL', width: "80", type: 'number',
      editable: true, align: 'center', headerAlign: 'left',
      valueSetter: (params) => {
        let data = {
          id: params.row?.id,
          userId: params.row?.userId,
          symbol: params.row?.symbol,
          tokenId: params.row?.tokenId,
          userTradeStrategy: params.row?.userTradeStrategy,
          createdOn: params.row?.createdOn,
          triggerPrice: params.row?.triggerPrice,
          quantity: params.row?.quantity,
          trailingStoplossAbsolute: params?.row?.trailingStoplossAbsolute,
          trailingStoplossPercent: params?.row?.trailingStoplossPercent,
          fixedStoplossAbsolute: null,
          fixedStoplossPercent: params.value
        }
        return data;
      }
    },
    {
      field: 'pClose', headerName: 'Close', type: 'number', align: 'right', headerAlign: 'left', width: 75,
      valueGetter: (params) => {
        const value = watchListLiveMap?.[params?.row?.tokenId]?.close_price / 100
        return toFixed2(value)
      }
    },
    {
      field: 'highPriceDay', headerName: 'High P day', type: 'number', align: 'right', headerAlign: 'left', width: 75,
      valueGetter: (params) => {
        const value = watchListLiveMap?.[params?.row?.tokenId]?.high_price_day / 100
        return toFixed2(value)
      }
    },
    {
      field: 'lowPriceDay', headerName: 'Low P day', type: 'number', align: 'right', headerAlign: 'left', width: 75,
      valueGetter: (params) => {
        const value = watchListLiveMap?.[params?.row?.tokenId]?.low_price_day / 100
        return toFixed2(value)
      }
    },

    {
      field: 'upperCircuit', headerName: 'U Circuit', type: 'number', align: 'right', headerAlign: 'left', width: 75,
      valueGetter: (params) => {
        const price = watchListLiveMap?.[params?.row?.tokenId]?.upper_circuit
        return toFixed2(price / 100)
      }
    },
    {
      field: 'lowerCircuit', headerName: 'L Circuit', type: 'number', align: 'right', headerAlign: 'left', width: 75,
      valueGetter: (params) => {
        const price = watchListLiveMap?.[params?.row?.tokenId]?.lower_circuit
        return toFixed2(price / 100)
      }
    },
    {
      field: '_52WHigh', headerName: '52WHigh', type: 'number', align: 'right', headerAlign: 'left', width: 75,
      valueGetter: (params) => {
        const price = watchListLiveMap?.[params?.row?.tokenId]?.fiftytwo_week_high
        return toFixed2(price / 100)
      }
    },
    {
      field: '_52WLow', headerName: '52WLow', type: 'number', align: 'right', headerAlign: 'left', width: 75,
      valueGetter: (params) => {
        const price = watchListLiveMap?.[params?.row?.tokenId]?.fiftytwo_week_low
        return toFixed2(price / 100)
      }
    },
    {
      field: 'pnl', headerName: 'PnL', type: 'number', align: 'right', headerAlign: 'left', width: 75,
      valueGetter: params => {
        return params?.row?.aggregatedPnl?.toFixed?.(2)
      }
    },
    {
      field: 'deleteAction', headerName: 'Del', width: 20,
      renderCell: (params) => (
        <IconButton size={'small'} aria-label="delete" onClick={event => deleteFromWatchList(event, params?.row)}>
          <DeleteIcon />
        </IconButton>
      )
    },
    {
      field: 'createdOn', headerName: 'Cr On', width: 80,
      valueGetter: (params) => {
        return params?.value?.split('T')[1].split('.')[0]
      }
    }
  ];

  const handleZerodhaAction = (event, row, action) => {
    const quantity = !!row.quantity ? row.quantity : 1
    const ltp = parseFloat(watchListLiveMap?.[row?.tokenId]?.last_traded_price) / 100
    const triggerPrice = action === "BUY" ? ltp - (ltp * 2.7 / 100) : ltp + (ltp * 2.7 / 100);
    const variety = "regular";
    const data = [
      {
        "variety": variety,
        "tradingsymbol": row.symbol,
        "exchange": "NSE",
        "product": "MIS",
        "validity": "DAY",
        "transaction_type": action,
        "order_type": "MARKET",
        "quantity": quantity,
        "readonly": false,
      },
      {
        "variety": variety,
        "tradingsymbol": row.symbol,
        "exchange": "NSE",
        "product": "MIS",
        "validity": "DAY",
        "transaction_type": action === "BUY" ? "SELL" : "BUY",
        "order_type": "SL-M",
        "quantity": quantity,
        "trigger_price": parseFloat(triggerPrice?.toFixed(2)),
        "readonly": false,
      },
    ]

    const form = formRef.current;
    form.action = "https://kite.zerodha.com/connect/basket";
    form.method = "POST";
    form.target = "_blank";

    form.elements["api_key"].value = "ohomgjw4o963d9az";
    form.elements["data"].value = JSON.stringify(data)

    form.submit();
  };

  const deleteFromWatchList = async (event, row) => {
    setIsLoadingWatchList(true)
    console.log('delete ', row)
    const endpoint = `/algo-trading/api/watchList/${row?.id}`;

    fetch(endpoint, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': user.credential
      },
      body: JSON.stringify({})
    })
      .then(response => {
        console.log(response)
        if (response.status >= 400) {
          response.text()
            .then(r => {
              console.error(r)
              setWarningMessage(JSON.parse(r)?.message)
              setOpen(true)
            })
          return
        }
        deleteWatchListIemFromUI(row?.id)

      }).finally(() => setIsLoadingWatchList(false));
  }

  async function shortListTrade(event, row, tradeAction) {
    const endpoint = `/algo-trading/api/watchList/short-list-trade`;
    const request = {};
    request.watchListId = row?.id;
    request.symbol = row?.symbol;
    request.tokenId = row?.tokenId;
    request.tradeStrategy = "TEST4";
    request.tradeAction = tradeAction;
    request.triggerPricePercentage = row?.triggerPrice;
    request.quantity = row?.quantity;
    request.trailingStoplossAbsolute = row?.trailingStoplossAbsolute;
    request.trailingStoplossPercent = row?.trailingStoplossPercent;
    request.fixedStoplossAbsolute = row?.fixedStoplossAbsolute;
    request.fixedStoplossPercent = row?.fixedStoplossPercent;

    // TODO: Fix column re-rendering and add UI validation
    if (request.triggerPrice && request.triggerPrice?.trim?.() !== '' && !isNumeric(request.triggerPrice)) {
      alert('Trigger price is not numeric value')
      return
    }

    fetch(endpoint, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': user.credential
      },
      body: JSON.stringify(request)
    }
    ).then(response => {
      setUpdateAllGrids(randomNumber())
      if (response.status >= 400) {
        response.text()
          .then(r => {
            console.error(r)
            setWarningMessage(r)
            setOpen(true)
          })
        return
      }
    });
  }

  const deleteShortListedTrade = id => {
    fetch(`/algo-trading/api/dh/short-listed-trades-active/${id}`, {
      headers: new Headers({
        'Authorization': user.credential
      }),
      method: 'DELETE'
    })
      .then(response => {
        if (response.status >= 400) {
          response.text()
            .then(r => {
              console.error(r)
              setWarningMessage(JSON.parse(r)?.message)
              setOpen(true)
            })
          return
        }
        setUpdateAllGrids(randomNumber())
        // Remove trade from UI
        setShortListedTrades(prev => prev?.filter(trade => trade?.id !== id))
      })
  }

  const calculateOpenTradePnl = (watchListTokenId, openTrade) => {
    try {
      const ltp = watchListLiveMap?.[watchListTokenId]?.last_traded_price / 100
      if (openTrade?.tradeAction === 'BUY')
        return (ltp - openTrade?.price) * openTrade?.quantity
      if (openTrade?.tradeAction === 'SELL')
        return (openTrade?.price - ltp) * openTrade?.quantity
    } catch (e) {
      console.error('Error calculating PnL for open trade', openTrade, e)
      return 0
    }
  }

  // Create array with watchlist, shortlisted and open trades linked together
  /*
    [
      {
        watchListId: 1,
        shortListedTrades: [
          {
            id: 1,,
            openTrades: [
              {
                id: 1
                price:4
              }
            ]
          }
        ]
      }
    ]

  */
  const processedWatchlistAndTrades = useMemo(() => {
    const result = [...watchList]

    result?.forEach(watchListItem => {
      // If we match by ID alone, then yesterday's open trade wont be visible
      // If we match by tokenId alone, then watchlistEntity token ID differs from actual token id
      // So we match by both
      watchListItem.shortListedTrades =
        shortListedTrades?.filter(t => t?.tokenId === watchListItem?.tokenId || t?.watchListId === watchListItem?.id)
      watchListItem.shortListedTrades?.forEach(shortListed => {
        shortListed.openTrades =
          openTrades?.filter(openTrade => openTrade?.tokenId === shortListed?.tokenId || openTrade?.shortlistedTradeId === shortListed?.id)
            ?.map(openTrade => ({
              ...openTrade, pnl: calculateOpenTradePnl(watchListItem?.tokenId, openTrade)
            }))
        shortListed.aggregatedPnl = shortListed.openTrades?.reduce((acc, openTrade) => {
          if (openTrade?.pnl)
            return acc + openTrade?.pnl
          return acc
        }, 0)
        watchListItem.aggregatedPnl = watchListItem.shortListedTrades?.reduce((acc, shortListed) => {
          if (shortListed.aggregatedPnl)
            return acc + shortListed.aggregatedPnl
          return acc
        }, 0)
      })
      // We cannot pass functions as prop because they would rerender and cause glitch while editing triggerPrice, etc
      // Hence passing functions as part of the row.
      watchListItem.functions = {}
      watchListItem.functions.deleteShortListedTrade = deleteShortListedTrade
    })
    return result;
  }, [watchList, shortListedTrades, openTrades, watchListLiveMap])

  // when watchList is refreshed from backend, values which are edited by user are erased
  // To solve this, this state will store watchlist data along with whatever data is edited inside row
  // This state is carefully updated with backend data in useEffect so that data edited by user is not lost 
  const [rows, setRows] = useState(processedWatchlistAndTrades);

  useEffect(() => {

    if (processedWatchlistAndTrades?.length === 0)
      return

    // Initally we need to fill rows with data from backend
    // processedWatchlistAndTrades contains latest data from backend
    // passing processedWatchlistAndTrades inside useState of rows does not work (maybe because it is useMemo)
    // To solve this we check if rows length is zero, if so, we consider it as initial render and fill in all the data
    if (rows?.length === 0) {
      setRows(getReorderedRows(processedWatchlistAndTrades))
      return
    }

    // Pull in updated data from processedWatchlistAndTrades
    let updatedRows = rows?.map(row => {
      // This latest row contains latest data from for watchlist item, in case strategy, token or other watchlist column is changed at backend
      const latestRow = processedWatchlistAndTrades?.find(r => r?.id === row?.id)
      return { ...row, ...latestRow }
    })

    // Pull in new watchlist items from processedWatchlistAndTrades
    // New rows are items which are present in processedWatchlistAndTrades but not in 'rows'
    const newRows = processedWatchlistAndTrades?.filter(row => !updatedRows?.find(r => r?.id === row?.id))

    updatedRows = [...updatedRows, ...newRows]

    // If watchlist items are deleted at backend then delete them from rows also
    // Items which are in 'rows' but are not in 'processedWatchlistAndTrades' are considered as deleted
    updatedRows = updatedRows?.filter(row => processedWatchlistAndTrades?.find(r => r?.id === row?.id))


    setRows(getReorderedRows(updatedRows))
  }, [processedWatchlistAndTrades])

  useEffect(() => {
    // When rows are updated, we need to recalculate total PnL
    const totalPnl = rows?.reduce((acc, row) => {
      if (row?.aggregatedPnl)
        return acc + row?.aggregatedPnl
      return acc
    }, 0)
    setTotalPnl(totalPnl)
  }, [rows])

  // This method will store user edited values inside 'rows' state 
  const handleProcessRowUpdate = (updatedRow, originalRow) => {
    const updatedRows = rows.map((row) =>
      row.id === updatedRow.id ? { ...row, ...updatedRow } : row
    );
    setRows(updatedRows);
    return updatedRow
  }

  const getReorderedRows = rows => {
    const itemsWithShortlisted = rows?.filter(row => row?.shortListedTrades?.length > 0)
    const itemsWithoutShortlisted = rows?.filter(row => row?.shortListedTrades?.length <= 0)
    return [...itemsWithShortlisted, ...itemsWithoutShortlisted]
  }


  return (
    <>
      <Grid>
        <Box
          sx={{
            bgcolor: 'white',
            width: '100%',
            '& .profit': {
              color: 'green',
            },
            '& .loss': {
              color: 'red',
            },
          }}
        >
          <DataGrid
            loading={isLoading}
            density={"compact"}
            getRowId={(row) => row?.id}
            getRowHeight={() => '1rem'}
            rows={rows}
            columns={columns}
            processRowUpdate={handleProcessRowUpdate}
            onProcessRowUpdateError={console.log}
            rowHeight={30}
            sx={{
              typography: {
                fontSize: 13
              },
            }}
            initialState={{
              sorting: {
                sortModel: [{ field: 'boughOn', sort: 'desc' }],
              },
              pagination: {
                paginationModel: {
                  pageSize: 50
                }
              }
            }}
            getCellClassName={(params) => {
              if (params.field === 'percentFromOpen' || params.field === 'percentFromClose' || params.field === 'ltp') {
                return getClassName(params, params.field)
              }
              return '';
            }}
            pageSizeOptions={[50, 100]}
            slots={{
              row: CustomRow
            }}
          />
        </Box>
      </Grid>
      <form ref={formRef} style={{ display: 'none' }} id="basket-form" method="post" action="https://kite.zerodha.com/connect/basket">
        <input type="hidden" name="api_key" />
        <input type="hidden" id="basket" name="data" value="" />
      </form>
    </>
  )
}

export default WatchListData;

const TableCellSmall = (props) => (<TableCell {...props}
  style={{
    fontSize: '0.8rem', borderBottom: "0.5px solid",
    borderRight: "0.5px solid", borderColor: 'lightcoral', textAlign: 'center', padding: 0, margin: 0
  }}
/>)
const DhShortlisted = ({ shortListedTrades, deleteShortListedTrade }) => {
  const [expandShortListedTrade, setExpandShortListedTrade] = useState(false)
  const doExpandShortListedTrade = () => setExpandShortListedTrade(prev => !prev)
  return (

    <TableContainer component={Box} style={{ width: '40%', backgroundColor: '#80b918', marginLeft: 50 }}>
      <Table size="small" aria-label="a dense table">
        <TableHead>
          <TableRow>
            <TableCellSmall>Symbol</TableCellSmall>
            <TableCellSmall>PnL</TableCellSmall>
            <TableCellSmall>Action</TableCellSmall>
            <TableCellSmall>Trigger Price</TableCellSmall>
            <TableCellSmall>Quantity</TableCellSmall>
            <TableCellSmall>Trailing Stoploss Abs</TableCellSmall>
            <TableCellSmall>Trailing Stoploss %</TableCellSmall>
            <TableCellSmall>Fixed Stoploss Abs</TableCellSmall>
            <TableCellSmall>Fixed Stoploss %</TableCellSmall>
            <TableCellSmall>Created On</TableCellSmall>
            <TableCellSmall>Del</TableCellSmall>
          </TableRow>
        </TableHead>
        <TableBody>
          {shortListedTrades?.map?.((row) => {
            const shortlistedTradeId = row?.id
            const openTrades = row?.openTrades
            return (
              <React.Fragment key={shortlistedTradeId}>
                <TableRow
                  sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
                >
                  <TableCellSmall component="th" scope="row" >
                    {openTrades?.length > 0 &&
                      <IconButton
                        size={'small'}
                        style={{ backgroundColor: '#fae1dd', paddingRight: 3, paddingLeft: 3 }}
                        onClick={() => doExpandShortListedTrade(shortlistedTradeId)}>
                        <Typography>{openTrades?.length}&nbsp;</Typography>
                      </IconButton>
                    }
                    {row?.symbol}
                  </TableCellSmall>
                  <TableCellSmall>{row?.aggregatedPnl}</TableCellSmall>
                  <TableCellSmall>{row?.tradeAction}</TableCellSmall>
                  <TableCellSmall>{row?.triggerPrice}</TableCellSmall>
                  <TableCellSmall>{row?.quantity}</TableCellSmall>
                  <TableCellSmall>{row?.trailingStoplossAbsolute}</TableCellSmall>
                  <TableCellSmall>{row?.trailingStoplossPercent}</TableCellSmall>
                  <TableCellSmall>{row?.fixedStoplossAbsolute}</TableCellSmall>
                  <TableCellSmall>{row?.fixedStoplossPercent}</TableCellSmall>
                  <TableCellSmall>{row?.createdOn}</TableCellSmall>
                  <TableCellSmall>
                    <IconButton size={'small'} aria-label="delete" onClick={event => deleteShortListedTrade(row.id)}
                      sx={{ margin: 0, padding: 0 }}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCellSmall>
                </TableRow>
                {openTrades?.length > 0 &&
                  <TableRow
                    sx={{ '&:last-child td, &:last-child th': { border: 0 }, backgroundColor: 'white' }}
                  >
                    <TableCellSmall colSpan={5}>
                      <Collapse in={expandShortListedTrade}>
                        <DhOpenTrades openTrades={openTrades} />
                      </Collapse>
                    </TableCellSmall>
                  </TableRow>
                }
              </React.Fragment>
            )
          })}
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const DhOpenTrades = ({ openTrades }) => (
  <TableContainer component={Box} style={{ margin: 0, padding: 0, marginLeft: 50, backgroundColor: '#fae1dd' }}>
    <Table size="small" aria-label="a dense table">
      <TableHead>
        <TableRow>
          <TableCellSmall>Symbol</TableCellSmall>
          <TableCellSmall>Action</TableCellSmall>
          <TableCellSmall>PnL</TableCellSmall>
          <TableCellSmall>Created On</TableCellSmall>
          <TableCellSmall>Order ID</TableCellSmall>
          <TableCellSmall>Acted On</TableCellSmall>
        </TableRow>
      </TableHead>
      <TableBody>
        {openTrades?.map?.((row) => (
          <TableRow
            key={row?.id}
            sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
          >
            <TableCellSmall component="th" scope="row">
              {row?.symbol}
            </TableCellSmall>
            <TableCellSmall >{row?.tradeAction}</TableCellSmall>
            <TableCellSmall >{row?.pnl}</TableCellSmall>
            <TableCellSmall >{row?.createdOn}</TableCellSmall>
            <TableCellSmall >{row?.orderId}</TableCellSmall>
            <TableCellSmall >{row?.actedOn}</TableCellSmall>
          </TableRow>
        )
        )}
      </TableBody>
    </Table>
  </TableContainer>
)